import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import { LogOut, LayoutDashboard, User as UserIcon, Target, ArrowRight, RotateCcw, CalendarClock, X } from "lucide-react";
import ArtizenIdCard from "@/components/ArtizenIdCard";
import { ProfileSection } from "@/components/dashboard/ProfilePortfolio";
import MilestonesSection from "@/components/dashboard/MilestonesSection";

const SECTIONS = [
  { k: "overview",   label: "Overview",   icon: LayoutDashboard },
  { k: "profile",    label: "Profile",    icon: UserIcon },
  { k: "milestones", label: "Milestones", icon: Target },
];

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = SECTIONS.find((s) => s.k === searchParams.get("tab"))?.k || "overview";
  const [section, setSection] = useState(initial);

  useEffect(() => {
    if (loading) return;
    if (!user) { loginWithGoogle("/dashboard"); return; }
  }, [user, loading]);

  const setTab = (k) => {
    setSection(k);
    const next = new URLSearchParams(searchParams);
    next.set("tab", k);
    setSearchParams(next, { replace: true });
  };

  if (loading) return <PageShell><div className="p-20 font-mono text-sm">Loading…</div></PageShell>;
  if (!user) return null;

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="ARTIST DASHBOARD" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-10 flex flex-wrap gap-6 items-end justify-between">
          <div>
            <SectionLabel>Welcome back, {user.name?.split(" ")[0]}</SectionLabel>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.9] tracking-tighter">Your <span className="hl-coral">control room.</span></h1>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="btn-pill"><LogOut size={14} /> Logout</button>
        </div>
      </section>

      <section>
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-10 grid lg:grid-cols-12 gap-px bg-border">
          {/* SIDEBAR */}
          <aside className="lg:col-span-3 bg-background p-5">
            <p className="meta-mono mb-4">— SECTIONS</p>
            <nav className="grid gap-1">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const active = section === s.k;
                return (
                  <button
                    key={s.k}
                    data-testid={`dash-nav-${s.k}`}
                    onClick={() => setTab(s.k)}
                    className={`flex items-center gap-3 px-4 py-3 border text-left transition ${active ? "border-primary bg-primary/10 text-primary" : "border-transparent text-white/80 hover:bg-white/[0.03]"}`}
                  >
                    <Icon size={15} />
                    <span className="font-mono text-xs tracking-[0.18em] uppercase">{s.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* CONTENT */}
          <div className="lg:col-span-9 bg-background p-5 md:p-8 min-h-[600px]">
            {section === "overview"   && <Overview />}
            {section === "profile"    && <ProfileSection />}
            {section === "milestones" && <MilestonesSection />}
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function Overview() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduling, setRescheduling] = useState(null); // booking object under reschedule flow

  const load = () => {
    setLoading(true);
    api.get("/bookings/me")
      .then(({ data }) => setBookings(data.bookings || []))
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const now = Date.now();
  const active = bookings.filter((b) => b.status !== "cancelled");
  const past     = active.filter((b) => new Date(b.slot_iso).getTime() <  now);
  const upcoming = active.filter((b) => new Date(b.slot_iso).getTime() >= now);

  const bookAgain = (b) => {
    const q = b.advisor_slug ? `?advisor=${encodeURIComponent(b.advisor_slug)}` : "";
    navigate(`/unfair-advantage${q}`);
  };

  const cancel = async (b) => {
    if (!window.confirm(`Cancel your session with ${b.advisor_name}?\n\nSessions are NON-REFUNDABLE per T&Cs. This just frees the slot for another member.`)) return;
    try {
      await api.post(`/bookings/${b.booking_id}/cancel`);
      toast.success("Session cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  return (
    <div>
      {/* ARTIZEN ID */}
      <p className="meta-mono mb-4">— ARTIZEN ID</p>
      <ArtizenIdCard />

      {/* UPCOMING SESSIONS */}
      <div className="mt-12">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <p className="meta-mono">— UPCOMING UNFAIR ADVANTAGE SESSIONS</p>
          <button onClick={() => navigate("/unfair-advantage")} className="btn-pill" data-testid="dash-book-new-cta">
            Book a session <ArrowRight size={13} />
          </button>
        </div>
        {loading ? (
          <p className="font-mono text-sm text-white/50">Loading bookings…</p>
        ) : upcoming.length === 0 ? (
          <div className="border border-border p-6 bg-background" data-testid="dash-upcoming-empty">
            <p className="text-white/70 text-sm">No upcoming sessions. Book an Unfair Advantage session to get started.</p>
          </div>
        ) : (
          <div className="grid gap-px bg-border" data-testid="dash-upcoming-list">
            {upcoming.map((b) => (
              <BookingRow
                key={b.booking_id || b.slot_iso}
                b={b}
                kind="upcoming"
                onReschedule={() => setRescheduling(b)}
                onCancel={() => cancel(b)}
              />
            ))}
          </div>
        )}
      </div>

      {/* PAST SESSIONS — with Book again */}
      <div className="mt-12">
        <p className="meta-mono mb-5">— PAST UNFAIR ADVANTAGE SESSIONS</p>
        {loading ? null : past.length === 0 ? (
          <div className="border border-border p-6 bg-background" data-testid="dash-past-empty">
            <p className="text-white/70 text-sm">Your session history will show up here.</p>
          </div>
        ) : (
          <div className="grid gap-px bg-border" data-testid="dash-past-list">
            {past.map((b) => (
              <BookingRow key={b.booking_id || b.slot_iso} b={b} kind="past" onAction={bookAgain} />
            ))}
          </div>
        )}
      </div>

      {rescheduling && (
        <RescheduleModal
          booking={rescheduling}
          onClose={() => setRescheduling(null)}
          onDone={() => { setRescheduling(null); load(); }}
        />
      )}
    </div>
  );
}

function BookingRow({ b, kind, onAction, onReschedule, onCancel }) {
  const d = new Date(b.slot_iso);
  const dateLabel = d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  const timeLabel = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
  const rescheduleUsed = Number(b.reschedule_count || 0) >= 1;
  return (
    <div className="bg-background p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`booking-row-${b.booking_id || b.slot_iso}`}>
      <div>
        <p className="meta-mono !text-primary">— {(b.advisor_name || b.advisor_slug || "Advisor").toUpperCase()}</p>
        <p className="font-display text-xl md:text-2xl font-bold mt-1">{dateLabel} · {timeLabel}</p>
        {b.what_to_discuss && <p className="text-white/60 text-sm mt-2 max-w-xl line-clamp-2">{b.what_to_discuss}</p>}
        {rescheduleUsed && kind === "upcoming" && (
          <p className="meta-mono mt-2 !text-white/45">— FREE RESCHEDULE USED · CONTACT HELLO@ANARTIZEN.COM FOR FURTHER CHANGES</p>
        )}
      </div>
      {kind === "past" ? (
        <button
          onClick={() => onAction(b)}
          data-testid={`book-again-${b.advisor_slug || b.booking_id}`}
          className="btn-pill btn-coral"
        >
          <RotateCcw size={13} /> Book again
        </button>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="meta-mono !text-secondary">— CONFIRMED</span>
          <button
            onClick={() => onReschedule?.(b)}
            disabled={rescheduleUsed}
            data-testid={`reschedule-${b.booking_id}`}
            className="btn-pill disabled:opacity-40 disabled:cursor-not-allowed"
            title={rescheduleUsed ? "You've used your one free reschedule. Email hello@anartizen.com." : "Reschedule this session"}
          >
            <CalendarClock size={13} /> Reschedule
          </button>
          <button
            onClick={() => onCancel?.(b)}
            data-testid={`cancel-${b.booking_id}`}
            className="btn-pill text-white/70 hover:text-primary"
            title="Cancel this session (non-refundable per T&Cs)"
          >
            <X size={13} /> Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function RescheduleModal({ booking, onClose, onDone }) {
  const [slots, setSlots] = useState([]);
  const [pick, setPick] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/advisors/${booking.advisor_slug}/slots`)
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [booking.advisor_slug]);

  const confirm = async () => {
    if (!pick) { toast.error("Pick a new slot"); return; }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/reschedule`, { slot_iso: pick });
      toast.success("Session rescheduled");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reschedule failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-5" data-testid="reschedule-modal">
      <div className="bg-background border border-border w-full max-w-2xl p-6 max-h-[85vh] overflow-auto scroll-thin">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="meta-mono !text-primary">— RESCHEDULE</p>
            <p className="font-display text-2xl md:text-3xl font-bold mt-1">{booking.advisor_name}</p>
            <p className="meta-mono !text-white/55 mt-2">
              Currently: {new Date(booking.slot_iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" })} IST
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="reschedule-close"><X size={20} /></button>
        </div>
        <p className="text-white/70 text-sm mb-4 leading-relaxed">
          Pick a new slot below. This is your <strong>one free reschedule</strong> — further changes require emailing <a href="mailto:hello@anartizen.com" className="text-primary underline">hello@anartizen.com</a>.
        </p>
        {loading ? (
          <p className="meta-mono !text-white/50">— LOADING SLOTS…</p>
        ) : slots.length === 0 ? (
          <p className="text-white/70 text-sm">No open slots for {booking.advisor_name} in the next 3 weeks. Please email hello@anartizen.com.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[300px] overflow-auto scroll-thin">
            {slots.map((s) => {
              const d = new Date(s);
              const label = `${d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" })} · ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" })}`;
              return (
                <button
                  key={s}
                  data-testid={`reschedule-slot-${s}`}
                  onClick={() => setPick(s)}
                  className={`p-2 border text-[10px] font-mono uppercase tracking-wider ${pick === s ? "border-primary bg-primary text-white" : "border-border hover:border-white text-white/80"}`}
                >{label}</button>
              );
            })}
          </div>
        )}
        <div className="flex gap-2 mt-6">
          <button onClick={confirm} disabled={!pick || busy} className="btn-pill btn-coral disabled:opacity-40" data-testid="reschedule-confirm">
            {busy ? "Rescheduling…" : "Confirm new slot"} <ArrowRight size={13} />
          </button>
          <button onClick={onClose} className="btn-pill">Cancel</button>
        </div>
      </div>
    </div>
  );
}
